import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Modal } from "../../components/ui/modal";
import { callAnalyticsApi, errorText } from "./api";
import { countChanges, FEATURE_LABELS, isNum, parseSamplePct } from "./format";
import type { FeatureKey, FeatureSettings } from "./types";
import { FEATURE_KEYS } from "./types";

type Draft = Record<FeatureKey, { available: boolean; pct: string }>;

const toDraft = (platform: FeatureSettings): Draft =>
  Object.fromEntries(FEATURE_KEYS.map((key) => [key, {
    available: platform[key]?.enabled !== false,
    pct: isNum(platform[key]?.sample_pct) ? String(platform[key]!.sample_pct) : "",
  }])) as Draft;

// "Available" is stored as null, the contract's meaning; an existing `true` is kept so saving
// never rewrites features the admin did not touch.
export function toPlatformSettings(draft: Draft, original: FeatureSettings): { settings: FeatureSettings; invalid: boolean } {
  let invalid = false;
  const settings: FeatureSettings = {};
  FEATURE_KEYS.forEach((key) => {
    const pct = parseSamplePct(draft[key].pct);
    if (pct === "invalid") invalid = true;
    settings[key] = {
      enabled: draft[key].available ? (original[key]?.enabled === true ? true : null) : false,
      sample_pct: pct === "invalid" ? null : pct,
    };
  });
  return { settings, invalid };
}

export function CallAnalyticsLimitsDialog({ userId, userName, onClose }: { userId: string; userName: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const config = useQuery({
    queryKey: ["call-analytics", "admin-config", userId],
    queryFn: () => callAnalyticsApi.adminConfig(userId),
  });
  const original = useMemo(() => config.data?.platform?.features ?? {}, [config.data]);
  const [draft, setDraft] = useState<Draft>(() => toDraft({}));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setDraft(toDraft(original)), [original]);

  const { settings, invalid } = toPlatformSettings(draft, original);
  const changeCount = countChanges(original, settings);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      // The whole map: the service replaces the platform scope's features on PUT.
      await callAnalyticsApi.saveAdminConfig(userId, settings);
      await queryClient.invalidateQueries({ queryKey: ["call-analytics", "admin-config", userId] });
      onClose();
    } catch (e) {
      setError(errorText(e, "The limits could not be saved."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Call analytics limits"
      subtitle={`For the organization ${userName} owns. Its admins configure within these limits.`}
      onClose={() => { if (!saving) onClose(); }}
      width="max-w-2xl"
      footer={(
        <>
          <button type="button" onClick={onClose} disabled={saving} className="rounded-lg border border-line px-4 py-2 text-sm font-semibold text-ink hover:bg-soft disabled:opacity-50">Cancel</button>
          <button
            type="button"
            onClick={save}
            disabled={saving || !config.data || invalid || changeCount === 0}
            className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
            {changeCount ? `Save ${changeCount} change${changeCount === 1 ? "" : "s"}` : "No changes"}
          </button>
        </>
      )}
    >
      {config.isLoading ? (
        <Loader2 className="animate-spin text-brand" aria-label="Loading limits" />
      ) : config.isError || !config.data ? (
        <p role="alert" className="text-sm text-danger">{errorText(config.error, "Limits could not be loaded.")}</p>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-ink-2">
            Call Analytics flag for this organization: <strong>{config.data.master_enabled ? "on" : "off"}</strong>.
            {config.data.master_enabled ? "" : " Limits are kept, but nothing runs until the flag is on and saved."}
          </p>
          <ul className="divide-y divide-line rounded-xl border border-line">
            {FEATURE_KEYS.map((key) => {
              const selectId = `ca-admin-${key}-available`;
              const pctId = `ca-admin-${key}-pct`;
              return (
                <li key={key} className="grid items-end gap-3 px-3 py-2 sm:grid-cols-[1fr_auto_auto]">
                  <span className="text-sm font-medium text-ink">{FEATURE_LABELS[key]}</span>
                  <div className="flex flex-col gap-1">
                    <label htmlFor={selectId} className="text-[11px] text-ink-3">{FEATURE_LABELS[key]} availability</label>
                    <select
                      id={selectId}
                      value={draft[key].available ? "available" : "unavailable"}
                      onChange={(e) => setDraft((d) => ({ ...d, [key]: { ...d[key], available: e.target.value === "available" } }))}
                      className="h-9 rounded-lg border border-line bg-elevated px-2 text-sm text-ink"
                    >
                      <option value="available">Available</option>
                      <option value="unavailable">Not available</option>
                    </select>
                  </div>
                  <div className="flex flex-col gap-1">
                    <label htmlFor={pctId} className="text-[11px] text-ink-3">{FEATURE_LABELS[key]} max sample %</label>
                    <input
                      id={pctId}
                      type="number"
                      min={0}
                      max={100}
                      placeholder="No limit"
                      value={draft[key].pct}
                      onChange={(e) => setDraft((d) => ({ ...d, [key]: { ...d[key], pct: e.target.value } }))}
                      className="h-9 w-28 rounded-lg border border-line bg-elevated px-2 text-sm text-ink"
                    />
                  </div>
                </li>
              );
            })}
          </ul>
          {invalid ? <p role="alert" className="text-sm text-danger">Max sample % must be between 0 and 100.</p> : null}
          {error ? <p role="alert" className="text-sm text-danger">{error}</p> : null}
        </div>
      )}
    </Modal>
  );
}
